import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import { PAGES } from '../constants/pages';
import {
  createApplicationStep,
  getApplication,
} from '../store/actions/application/applicationActions';
import useGtmAddressDoneHook from '../util/hooks/useGtmAddressDoneHook';
import useGtmSecureHook from '../util/hooks/useGtmSecureHook';
import useApplicationUid from './useApplicationUid';

// This hook is created as a supplement to OnboardingCta component
// It takes in next path and onModalClose, and gives back a onSuccess function
// It Returns a onSuccess function -> Read comments in function body
const useStepPost = ({ nextPath, skipStepPath, onModalClose, handleNext }) => {
  const history = useHistory();
  const { ApplicationUid } = useApplicationUid();
  const { gtmApplicationSecureHook } = useGtmSecureHook();
  const { gtmAddressDoneHook } = useGtmAddressDoneHook();

  const dispatch = useDispatch();

  // Calls the Api and updates the active Step, also on Success calls
  const updateStep = (path) => {
    const gtmSuccess = () => {
      if (path === PAGES.ADDRESS) {
        gtmApplicationSecureHook();
        return;
      }

      if (
        path === PAGES.FUNDING.RETIREMENT_FUNDING.ACCOUNT ||
        path === PAGES.FUNDING.NORMAL_FUNDING.ACCOUNT
      ) {
        gtmAddressDoneHook();
      }
    };

    dispatch(
      createApplicationStep({
        ApplicationUid,
        data: {
          Step: path,
        },
        onSuccess: () => {
          gtmSuccess();
          history.push(path);
        },
      }),
    );
  };

  const onModalNextStep = () => {
    dispatch(
      getApplication({
        data: { ApplicationUid },
        onSuccess: () => {
          handleNext();
        },
      }),
    );
  };

  const onSuccess = (overwriteRoute) => {
    // If nextPath is defined it means this hook is used as a continue on onboarding step, it should update the step.

    if (overwriteRoute) {
      updateStep(overwriteRoute);
      return;
    }

    if (nextPath) {
      updateStep(nextPath);
      return;
    }

    // If next path is not defined then this button is used on a modal, and it shouldn't update the step.
    if (!nextPath) {
      dispatch(getApplication({ data: { ApplicationUid } }));
      onModalClose();
    }
  };

  const onCompleteAppStep = (completeAppStep) => {
    // If nextPath is defined it means this hook is used as a continue on onboarding step, it should update the step.
    if (completeAppStep) {
      updateStep(completeAppStep);
    }
  };

  const onSkipStep = () => {
    // If nextPath is defined it means this hook is used as a continue on onboarding step, it should update the step.
    if (skipStepPath) {
      updateStep(skipStepPath);
      return;
    }

    // If next path is not defined then this button is used on a modal, and it shouldn't update the step.
    if (!skipStepPath) {
      dispatch(getApplication({ data: { ApplicationUid } }));
      if (onModalClose) {
        onModalClose();
      }
    }
  };

  return { onSuccess, onSkipStep, onModalNextStep, onCompleteAppStep };
};

useStepPost.propTypes = {
  nextPath: PropTypes.string,
  skipStepPath: PropTypes.string,
  onModalClose: PropTypes.string,
  handleNext: PropTypes.string,
};

export default useStepPost;
